import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
} from "react";
import { motion, AnimatePresence } from "motion/react";

import stacyLogoSvg from "../assets/marca/logo-alternativo.svg";
import formLogoSvg from "../assets/emblema-3-red.svg";
import successLogoSvg from "../assets/emblema-3.svg";
import capaIconSvg from "../assets/marca/logo-small-beige.svg";
import capaPuntoSvg from "../assets/marca/logo-small-beige.svg";

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback?: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
      theme?: "light" | "dark" | "auto";
    }
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
};

const JOB_OPTIONS = [
  "Cocina",
  "Atención al público",
  "Administrativo",
] as const;

function ChevronDownIcon({ open }: { open: boolean }) {
  return (
    <motion.svg
      animate={{ rotate: open ? 180 : 0 }}
      transition={{ duration: 0.2 }}
      className="h-5 w-5 shrink-0"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="#BA4643"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </motion.svg>
  );
}

const EMAIL_ENDPOINT = "https://email-mailersend.stacyburgersoficial.workers.dev/";
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const ALLOWED_FILE_EXTENSIONS = [".pdf", ".doc", ".docx"];
const MAX_CV_SIZE_BYTES = 10 * 1024 * 1024;

function UploadIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 19.5 19.5">
      <path
        d="M9.75 1.5V12M9.75 1.5L13 4.5M9.75 1.5L6.5 4.5M18 13.5V16C18 17.1046 17.1046 18 16 18H3.5C2.39543 18 1.5 17.1046 1.5 16V13.5"
        stroke="#F2F1DE"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="3"
      />
    </svg>
  );
}

function SubmitArrowIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 18.0015 18.0002">
      <path
        d="M4.75 7.75H3.95C2.8299 7.75 2.26984 7.75 1.84202 7.96799C1.4657 8.15973 1.15973 8.46569 0.967988 8.84202C0.750001 9.26984 0.750001 9.8299 0.750001 10.95V14.05C0.750001 15.1701 0.750001 15.7302 0.967988 16.158C1.15973 16.5343 1.4657 16.8403 1.84202 17.032C2.26984 17.25 2.8299 17.25 3.95 17.25H4.75M4.75 7.75H4.79312C5.22573 7.75 5.44204 7.75 5.62381 7.67576C5.78424 7.61024 5.92489 7.50414 6.03197 7.36788C6.15328 7.21351 6.21271 7.00552 6.33156 6.58955L7.7931 1.47415C7.91557 1.04552 8.30734 0.75 8.75313 0.75C9.85597 0.75 10.75 1.64403 10.75 2.74688V5.15C10.75 5.71005 10.75 5.99008 10.859 6.20399C10.9549 6.39215 11.1078 6.54513 11.296 6.64101C11.5099 6.75 11.7899 6.75 12.35 6.75H13.68C15.1202 6.75 15.836 6.75 16.32 7.04991C16.744 7.31263 17.0639 7.7243 17.2 8.20416C17.3554 8.75194 17.1457 9.44433 16.75 10.8291L15.58 14.9291C15.3423 15.761 15.2226 16.177 14.98 16.4858C14.7658 16.7583 14.4909 16.9705 14.17 17.1015C13.8065 17.25 13.3752 17.25 12.51 17.25H4.75M4.75 7.75V17.25"
        stroke="#F2F1DE"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

export default function Team() {
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as
    | string
    | undefined;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    cv: null as File | null,
  });

  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    cv: "",
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileError, setTurnstileError] = useState("");
  const [formCardHeight, setFormCardHeight] = useState<number>(0);
  const [shouldLoadTurnstile, setShouldLoadTurnstile] = useState(false);

  const sectionRef = useRef<HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const formCardRef = useRef<HTMLDivElement>(null);

  const [isPositionOpen, setIsPositionOpen] = useState(false);
  const positionDropdownRef = useRef<HTMLDivElement>(null);

  const activateTurnstileLoad = () => {
    setShouldLoadTurnstile(true);
  };

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || shouldLoadTurnstile) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadTurnstile(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.15,
        rootMargin: "200px 0px",
      }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [shouldLoadTurnstile]);


  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (!positionDropdownRef.current?.contains(target)) {
        setIsPositionOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsPositionOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (
      !shouldLoadTurnstile ||
      !turnstileSiteKey ||
      !turnstileContainerRef.current
    ) {
      return;
    }

    const win = window as Window & { turnstile?: TurnstileApi };
    let cancelled = false;

    const renderWidget = () => {
      if (
        cancelled ||
        !win.turnstile ||
        !turnstileContainerRef.current ||
        turnstileWidgetIdRef.current
      ) {
        return;
      }

      turnstileWidgetIdRef.current = win.turnstile.render(
        turnstileContainerRef.current,
        {
          sitekey: turnstileSiteKey,
          theme: "light",
          callback: (token) => {
            setTurnstileToken(token);
            setTurnstileError("");
          },
          "expired-callback": () => {
            setTurnstileToken("");
            setTurnstileError("La verificación expiró. Completala otra vez.");
          },
          "error-callback": () => {
            setTurnstileToken("");
            setTurnstileError("No se pudo validar. Intentá nuevamente.");
          },
        }
      );
    };

    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${TURNSTILE_SCRIPT_SRC}"]`
    );

    const script =
      existingScript ??
      Object.assign(document.createElement("script"), {
        src: TURNSTILE_SCRIPT_SRC,
        async: true,
        defer: true,
      });

    script.addEventListener("load", renderWidget);

    if (!existingScript) {
      document.head.appendChild(script);
    } else if (win.turnstile) {
      renderWidget();
    }

    return () => {
      cancelled = true;
      script.removeEventListener("load", renderWidget);

      if (turnstileWidgetIdRef.current && win.turnstile) {
        win.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [turnstileSiteKey, shouldLoadTurnstile]);

  useLayoutEffect(() => {
    if (!formCardRef.current || isSubmitted) return;

    const element = formCardRef.current;

    const updateHeight = () => {
      setFormCardHeight(element.offsetHeight);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);
    window.addEventListener("resize", updateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [
    isSubmitted,
    formData.cv,
    fieldErrors.name,
    fieldErrors.email,
    fieldErrors.phone,
    fieldErrors.position,
    fieldErrors.cv,
    turnstileError,
    submitError,
    shouldLoadTurnstile,
  ]);

  const validateName = (value: string) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) return "Ingresa tu nombre completo.";
    if (trimmedValue.length < 3) {
      return "El nombre debe tener al menos 3 caracteres.";
    }

    return "";
  };

  const validateEmail = (value: string) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) return "Ingresa tu email.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) {
      return "Ingresa un email válido.";
    }

    return "";
  };

  const validatePhone = (value: string) => {
    const normalizedValue = value.replace(/\s+/g, "");

    if (!normalizedValue) return "Ingresa tu número de teléfono.";
    if (!/^\+?[0-9]{8,15}$/.test(normalizedValue)) {
      return "Ingresa un teléfono válido.";
    }

    return "";
  };

  const validatePosition = (value: string) => {
    if (!value.trim()) {
      return "Selecciona el puesto al que quieres postularte.";
    }

    return "";
  };

  const validateCv = (file: File | null) => {
    if (!file) return "Adjuntá tu CV.";

    const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;

    if (!ALLOWED_FILE_EXTENSIONS.includes(extension)) {
      return "El CV debe ser PDF, DOC o DOCX.";
    }

    if (file.size > MAX_CV_SIZE_BYTES) {
      return "El CV no puede superar 10 MB.";
    }

    return "";
  };

  const validateForm = () => {
    const nextErrors = {
      name: validateName(formData.name),
      email: validateEmail(formData.email),
      phone: validatePhone(formData.phone),
      position: validatePosition(formData.position),
      cv: validateCv(formData.cv),
    };

    setFieldErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const applySelectedFile = (file: File | null) => {
    setFieldErrors((currentErrors) => ({ ...currentErrors, cv: "" }));
    setFormData((current) => ({ ...current, cv: file }));
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    activateTurnstileLoad();

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      applySelectedFile(files[0]);
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    activateTurnstileLoad();

    const files = e.target.files;
    if (files && files[0]) {
      applySelectedFile(files[0]);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError("");
    activateTurnstileLoad();

    if (!validateForm()) return;

    if (!turnstileSiteKey) {
      setTurnstileError("Falta configurar VITE_TURNSTILE_SITE_KEY.");
      return;
    }

    if (!turnstileToken) {
      setTurnstileError(
        "Completá la validación para verificar que no sos un robot."
      );
      return;
    }

    const body = new FormData();
    body.append("name", formData.name.trim());
    body.append("email", formData.email.trim());
    body.append("phone", formData.phone.trim());
    body.append("position", formData.position.trim());
    body.append("cv", formData.cv as Blob, formData.cv?.name);

    setIsSubmitting(true);

    try {
      const response = await fetch(EMAIL_ENDPOINT, {
        method: "POST",
        body,
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const result = (await response.json()) as { ok?: boolean };
      if (!result.ok) {
        throw new Error("Backend did not confirm email delivery");
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting form", error);
      setSubmitError("No pudimos confirmar el envío de tu CV. Intentá nuevamente.");

      const win = window as Window & { turnstile?: TurnstileApi };
      setTurnstileToken("");

      if (turnstileWidgetIdRef.current && win.turnstile) {
        win.turnstile.reset(turnstileWidgetIdRef.current);
      }

      return;
    } finally {
      setIsSubmitting(false);
    }

    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: "", email: "", phone: "", position: "", cv: null });
      setFieldErrors({ name: "", email: "", phone: "", position: "", cv: "" });
      setTurnstileToken("");
      setTurnstileError("");
      setSubmitError("");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      const win = window as Window & { turnstile?: TurnstileApi };
      if (turnstileWidgetIdRef.current && win.turnstile) {
        win.turnstile.reset(turnstileWidgetIdRef.current);
      }
    }, 5000);
  };

  return (
    <section
      ref={sectionRef}
      id="equipo"
      className="relative w-full bg-[#f2f1de] px-4 py-20 sm:px-8 md:px-10 lg:px-10 xl:px-16"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 0.8 }}
        className="mx-auto max-w-[1920px]"
      >
        <div className="grid grid-cols-1 gap-10 xl:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)] xl:items-center xl:gap-16">
          <div className="flex w-full min-w-0 max-w-[760px] flex-col justify-center gap-4 self-center">
            <div className="w-[110px] sm:w-[128px] lg:w-[144px]">
              <img
                src={stacyLogoSvg}
                alt="Stacy Logo"
                className="h-auto w-full object-contain"
              />
            </div>

            <h2 className="m-0 font-['Excessive_Extra_Condensed'] text-[clamp(5rem,12vw,10.5rem)] leading-[0.9] tracking-[0.02em] text-[#181819]">
              <span className="block">NO SOLO TRABAJES.</span>
              <span className="block">JUGÁ DE LOCAL.</span>
            </h2>

            <p className="m-0 max-w-[40rem] font-['Roboto_Mono',monospace] text-[clamp(1.1rem,2.2vw,2rem)] leading-[1.14] text-[#181819]">
              Stacy es ritmo, energía y{" "}
              <span className="font-bold">trabajo en equipo.</span>
            </p>
          </div>

          <div className="w-full max-w-[1024px] xl:justify-self-end">
            <div
              className="w-full"
              style={
                formCardHeight
                  ? { minHeight: `${formCardHeight}px` }
                  : undefined
              }
            >
              <AnimatePresence mode="wait">
                {!isSubmitted ? (
                  <motion.div
                    key="form"
                    ref={formCardRef}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                    className="w-full rounded-[24px] bg-[#f5f4e5] p-6 shadow-[0px_14px_64px_0px_rgba(24,24,25,0.12),0px_8px_22px_0px_rgba(24,24,25,0.12)] sm:p-8"
                  >
                    <form
                      onSubmit={handleSubmit}
                      onFocusCapture={activateTurnstileLoad}
                      onPointerDownCapture={activateTurnstileLoad}
                      className="space-y-6 sm:space-y-8"
                    >
                      <div className="flex w-full items-start justify-between gap-4">
                        <h3 className="m-0 min-w-0 flex-1 font-['Excessive_Extra_Condensed'] text-[clamp(3.5rem,9vw,7.5rem)] leading-[0.9] tracking-[0.02em] text-[#ba4643]">
                          SUMATE AL EQUIPO
                        </h3>

                        <div className="hidden w-[92px] shrink-0 sm:block lg:w-[130px]">
                          <img
                            src={formLogoSvg}
                            alt="Join the team"
                            className="h-auto w-full object-contain"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div className="min-w-0">
                          <label className="mb-2 block font-['Roboto_Mono',monospace] text-xs tracking-wider text-[#181819]">
                            NOMBRE
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => {
                              setFormData((current) => ({
                                ...current,
                                name: e.target.value,
                              }));
                              setFieldErrors((currentErrors) => ({
                                ...currentErrors,
                                name: "",
                              }));
                            }}
                            placeholder="Ingresa tu nombre completo"
                            className={`w-full rounded-lg border-4 bg-transparent px-4 py-4 font-['Roboto_Mono',monospace] font-bold text-[#d18381] placeholder:text-[#d18381] transition-colors focus:outline-none ${fieldErrors.name
                              ? "border-[#ba4643]"
                              : formData.name
                                ? "border-red-500"
                                : "border-[#d18381] focus:border-red-500"
                              }`}
                            required
                          />
                          {fieldErrors.name ? (
                            <p className="mb-0 mt-2 font-['Roboto_Mono',monospace] text-sm text-[#ba4643]">
                              {fieldErrors.name}
                            </p>
                          ) : null}
                        </div>

                        <div className="min-w-0">
                          <label className="mb-2 block font-['Roboto_Mono',monospace] text-xs tracking-wider text-[#181819]">
                            EMAIL
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => {
                              setFormData((current) => ({
                                ...current,
                                email: e.target.value,
                              }));
                              setFieldErrors((currentErrors) => ({
                                ...currentErrors,
                                email: "",
                              }));
                            }}
                            placeholder="Ingresa tu email"
                            className={`w-full rounded-lg border-4 bg-transparent px-4 py-4 font-['Roboto_Mono',monospace] font-bold text-[#d18381] placeholder:text-[#d18381] transition-colors focus:outline-none ${fieldErrors.email
                              ? "border-[#ba4643]"
                              : formData.email
                                ? "border-red-500"
                                : "border-[#d18381] focus:border-red-500"
                              }`}
                            required
                          />
                          {fieldErrors.email ? (
                            <p className="mb-0 mt-2 font-['Roboto_Mono',monospace] text-sm text-[#ba4643]">
                              {fieldErrors.email}
                            </p>
                          ) : null}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div className="min-w-0">
                          <label className="mb-2 block font-['Roboto_Mono',monospace] text-xs tracking-wider text-[#181819]">
                            TELÉFONO
                          </label>
                          <input
                            type="text"
                            value={formData.phone}
                            onChange={(e) => {
                              setFormData((current) => ({
                                ...current,
                                phone: e.target.value,
                              }));
                              setFieldErrors((currentErrors) => ({
                                ...currentErrors,
                                phone: "",
                              }));
                            }}
                            placeholder="Ingresa tu número de teléfono"
                            className={`w-full rounded-lg border-4 bg-transparent px-4 py-4 font-['Roboto_Mono',monospace] font-bold text-[#d18381] placeholder:text-[#d18381] transition-colors focus:outline-none ${fieldErrors.phone
                              ? "border-[#ba4643]"
                              : formData.phone
                                ? "border-red-500"
                                : "border-[#d18381] focus:border-red-500"
                              }`}
                            required
                          />
                          {fieldErrors.phone ? (
                            <p className="mb-0 mt-2 font-['Roboto_Mono',monospace] text-sm text-[#ba4643]">
                              {fieldErrors.phone}
                            </p>
                          ) : null}
                        </div>

                        <div className="min-w-0">
                          <label className="mb-2 block font-['Roboto_Mono',monospace] text-xs tracking-wider text-[#181819]">
                            PUESTO
                          </label>

                          <div ref={positionDropdownRef} className="relative">
                            <button
                              type="button"
                              onClick={() => {
                                activateTurnstileLoad();
                                setIsPositionOpen((current) => !current);
                              }}
                              className={`flex w-full items-center justify-between rounded-lg border-4 bg-transparent px-4 py-4 text-left font-['Roboto_Mono',monospace] font-bold transition-colors focus:outline-none ${fieldErrors.position
                                ? "border-[#ba4643]"
                                : formData.position
                                  ? "border-red-500"
                                  : "border-[#d18381] focus:border-red-500"
                                } ${formData.position ? "text-[#181819]" : "text-[#d18381]"}`}
                              aria-haspopup="listbox"
                              aria-expanded={isPositionOpen}
                            >
                              <span>
                                {formData.position || "Selecciona un puesto"}
                              </span>
                              <ChevronDownIcon open={isPositionOpen} />
                            </button>

                            <AnimatePresence>
                              {isPositionOpen ? (
                                <motion.div
                                  initial={{ opacity: 0, y: -6 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -4 }}
                                  transition={{ duration: 0.16, ease: [0.4, 0, 0.2, 1] }}
                                  className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-lg border-4 border-[#d18381] bg-[#f5f4e5] shadow-[0px_14px_40px_0px_rgba(24,24,25,0.12),0px_8px_18px_0px_rgba(24,24,25,0.10)]"
                                >
                                  <div role="listbox" className="flex flex-col">
                                    {JOB_OPTIONS.map((option, index) => {
                                      const isSelected = formData.position === option;
                                      const isFirst = index === 0;
                                      const isLast = index === JOB_OPTIONS.length - 1;

                                      return (
                                        <button
                                          key={option}
                                          type="button"
                                          onClick={() => {
                                            setFormData((current) => ({
                                              ...current,
                                              position: option,
                                            }));
                                            setFieldErrors((currentErrors) => ({
                                              ...currentErrors,
                                              position: "",
                                            }));
                                            setIsPositionOpen(false);
                                          }}
                                          className={[
                                            "flex w-full items-center px-4 py-4 text-left font-['Roboto_Mono',monospace] text-sm font-bold transition-colors",
                                            isSelected
                                              ? "bg-[#BA4643] text-[#F2F1DE]"
                                              : "bg-[#f5f4e5] text-[#181819] hover:bg-[#d18381]/12",
                                            !isLast ? "border-b border-[#d18381]/35" : "",
                                            isFirst ? "rounded-t-[4px]" : "",
                                            isLast ? "rounded-b-[4px]" : "",
                                          ].join(" ")}
                                        >
                                          {option}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </motion.div>
                              ) : null}
                            </AnimatePresence>
                          </div>

                          {fieldErrors.position ? (
                            <p className="mb-0 mt-2 font-['Roboto_Mono',monospace] text-sm text-[#ba4643]">
                              {fieldErrors.position}
                            </p>
                          ) : null}
                        </div>
                      </div>

                      <div>
                        <label className="mb-2 block font-['Roboto_Mono',monospace] text-xs tracking-wider text-[#181819]">
                          CV/RESUMEN
                        </label>

                        <div
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          onClick={() => {
                            activateTurnstileLoad();
                            fileInputRef.current?.click();
                          }}
                          className={`relative w-full cursor-pointer rounded-lg border-4 border-dashed px-6 py-8 transition-all duration-300 hover:border-red-500 ${fieldErrors.cv
                            ? "border-[#ba4643]"
                            : isDragging
                              ? "border-red-500 bg-[#d18381]/10"
                              : formData.cv
                                ? "border-red-500"
                                : "border-[#d18381]"
                            }`}
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileSelect}
                            className="hidden"
                          />

                          <div className="flex flex-col items-center gap-4">
                            <div className="rounded-lg bg-[#d18381] p-4">
                              <UploadIcon />
                            </div>

                            <p className="m-0 max-w-[303px] text-center font-['Roboto_Mono',monospace] font-medium text-[#d18381]">
                              {formData.cv ? (
                                <span className="break-words text-[#181819]">
                                  {formData.cv.name}
                                </span>
                              ) : (
                                "Arrastrá y soltá o clickeá para subir tu CV"
                              )}
                            </p>
                          </div>
                        </div>

                        {fieldErrors.cv ? (
                          <p className="mb-0 mt-2 font-['Roboto_Mono',monospace] text-sm text-[#ba4643]">
                            {fieldErrors.cv}
                          </p>
                        ) : (
                          <p className="mb-0 mt-2 font-['Roboto_Mono',monospace] text-xs text-[#6b5c5a]">
                            Formatos permitidos: PDF, DOC y DOCX. Máximo 10 MB.
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        {turnstileSiteKey ? (
                          shouldLoadTurnstile ? (
                            <div
                              ref={turnstileContainerRef}
                              className="min-h-[70px] overflow-hidden rounded-lg"
                            />
                          ) : (
                            <div className="min-h-[70px] rounded-lg border border-[#d18381]/35 bg-[#f2f1de] px-4 py-5">
                              <p className="m-0 font-['Roboto_Mono',monospace] text-xs text-[#6b5c5a]">
                                La verificación se cargará al interactuar con el
                                formulario.
                              </p>
                            </div>
                          )
                        ) : (
                          <p className="m-0 font-['Roboto_Mono',monospace] text-sm text-[#ba4643]">
                            Configura <code>VITE_TURNSTILE_SITE_KEY</code> para activar
                            Cloudflare Turnstile.
                          </p>
                        )}

                        {turnstileError ? (
                          <p className="m-0 font-['Roboto_Mono',monospace] text-sm text-[#ba4643]">
                            {turnstileError}
                          </p>
                        ) : null}

                        {submitError ? (
                          <p className="m-0 font-['Roboto_Mono',monospace] text-sm text-[#ba4643]">
                            {submitError}
                          </p>
                        ) : null}
                      </div>

                      <motion.button
                        type="submit"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex w-full cursor-pointer items-center justify-center gap-3 rounded-lg border-0 bg-[#113f7f] px-6 py-4 font-['Roboto_Mono',monospace] text-lg font-medium tracking-[0.08em] text-[#f2f1de] transition-colors hover:bg-[#0d2f5f] disabled:cursor-not-allowed disabled:opacity-60 sm:text-xl"
                        disabled={!turnstileSiteKey || isSubmitting}
                      >
                        {isSubmitting ? "ENVIANDO..." : "QUIERO JUGAR EN STACY"}
                        <SubmitArrowIcon />
                      </motion.button>
                    </form>
                  </motion.div>
                ) : (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="flex w-full flex-col items-center justify-center rounded-[24px] bg-[#ba4643] p-8 shadow-[0px_14px_64px_0px_rgba(24,24,25,0.12),0px_8px_22px_0px_rgba(24,24,25,0.12)] sm:p-12"
                    style={
                      formCardHeight
                        ? { minHeight: `${formCardHeight}px` }
                        : { minHeight: "500px" }
                    }
                  >
                    <div className="relative flex w-full max-w-[600px] flex-col items-center gap-4">
                      <motion.div
                        className="relative w-[104px] sm:w-[128px]"
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          delay: 0.15,
                          duration: 0.5,
                          ease: [0.34, 1.56, 0.64, 1],
                        }}
                      >
                        <img
                          src={successLogoSvg}
                          alt="Success Logo"
                          className="h-auto w-full object-contain"
                        />
                      </motion.div>

                      <motion.div
                        className="m-0 text-center font-['Excessive_Extra_Condensed'] text-[clamp(4rem,9vw,7.5rem)] leading-[0.9] tracking-[0.02em] text-[#f2f1de]"
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          delay: 0.25,
                          duration: 0.6,
                          ease: [0.34, 1.56, 0.64, 1],
                        }}
                      >
                        <p className="mb-0">GRACIAS POR</p>
                        <p className="mb-0">QUERER SUMARTE</p>
                      </motion.div>

                      <motion.div
                        className="m-0 w-full max-w-[303px] text-center font-['Roboto_Mono',monospace] text-base font-medium leading-relaxed tracking-wider text-[#f2f1de]"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          delay: 0.4,
                          duration: 0.5,
                          ease: [0.34, 1.56, 0.64, 1],
                        }}
                      >
                        <p className="mb-0 font-normal">Tu CV ya está en juego.</p>
                        <p className="mb-2">&nbsp;</p>
                        <p className="mb-0 font-bold">Hablamos pronto.</p>
                      </motion.div>

                      <motion.div
                        className="relative mt-4 h-[30.526px] w-[33.164px] shrink-0"
                        initial={{ opacity: 0, scale: 0.3 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          delay: 0.55,
                          duration: 0.5,
                          ease: [0.34, 1.56, 0.64, 1],
                        }}
                      >
                        <div className="absolute inset-[0_2.88%_0_0]">
                          <img src={capaIconSvg} alt="" className="h-full w-full" />
                        </div>
                        <div className="absolute inset-[61.38%_0_29.49%_91.32%]">
                          <img
                            src={capaPuntoSvg}
                            alt=""
                            className="h-full w-full"
                          />
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}